import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  BASIS_LABEL, adjustmentWarnings, lossOrDamageTotal, payslipHtml, periodWarnings,
  type AdjustmentBasis, type PayAdjustment, type PayslipLine,
} from "@core";

import type { EmployerView } from "../lib/employer";
import {
  loadPayslip, monthPeriod, saveAdjustment, type PayslipResult,
} from "../lib/payslip";

/**
 * The payslip, on the desk.
 *
 * BCEA s33(1) requires an employer to hand over written particulars every time
 * somebody is paid, and this is the side of the product where paper actually
 * gets made — a laptop has a printer attached and a phone does not.
 *
 * The numbers are `buildPayslip` in core, and the printed page is
 * `payslipHtml` from the same module the app prints. Nothing about the
 * document is re-implemented here; if it were, the employer's copy and the
 * worker's copy could drift, and the whole point of a payslip is that there is
 * only one of it.
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const BASES: (AdjustmentBasis | null)[] = [
  "agreed_in_writing", "required_by_law", "court_order", "loss_or_damage", "other", null,
];

function hoursLabel(value: number): string {
  const whole = Math.floor(value);
  const mins = Math.round((value - whole) * 60);
  return mins === 0 ? `${whole}h` : `${whole}h ${String(mins).padStart(2, "0")}m`;
}

export function Payslip({ ctx }: { ctx: EmployerView }) {
  const people = useMemo(() => ctx.employees, [ctx.employees]);
  const [employeeId, setEmployeeId] = useState(people[0]?.id ?? "");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [result, setResult] = useState<PayslipResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    if (!employeeId) return () => {};
    let cancelled = false;
    setLoading(true);
    loadPayslip(employeeId, year, month, { name: ctx.orgName, timezone: ctx.timezone })
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [employeeId, year, month, ctx.orgName, ctx.timezone]);

  useEffect(load, [load]);

  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: ctx.currency,
    minimumFractionDigits: 2,
  });

  const slip = result?.slip ?? null;

  const warnings = slip
    ? periodWarnings({
        gross: slip.gross,
        additions: slip.additions.reduce((s, a) => s + a.amount, 0),
        deductions: slip.deductions.reduce((s, d) => s + d.amount, 0),
        net: slip.net,
      })
    : [];

  /**
   * Print the real page, not the screen.
   *
   * An iframe rather than window.open, because a popup blocker will silently
   * swallow the second and the employer is left pressing a button that does
   * nothing. The frame is removed once the dialog closes.
   */
  function print() {
    if (!slip) return;
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) { frame.remove(); return; }
    doc.open();
    doc.write(payslipHtml(slip));
    doc.close();
    const go = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      // Safari fires afterprint late; a timeout is the portable cleanup.
      window.setTimeout(() => frame.remove(), 1000);
    };
    if (doc.readyState === "complete") go();
    else frame.onload = go;
  }

  if (people.length === 0) {
    return (
      <div className="panel">
        <div className="empty">
          There is nobody to make a payslip for yet.
          <br />
          Add somebody on the People page first.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Payslip</h1>
          <p>
            What the period comes to, worked out from the register. South African law requires
            written particulars each time somebody is paid — this is that document.
          </p>
        </div>
      </div>

      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">Statement of earnings</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <select
              className="compact" value={employeeId} aria-label="Person"
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              {people.map((p) => <option key={p.id} value={p.id}>{p.fullName}</option>)}
            </select>
            <select
              className="compact" value={month} aria-label="Month"
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
            </select>
            <select
              className="compact" value={year} aria-label="Year"
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="empty">Working it out…</div>
        ) : !slip ? (
          <div className="empty">That month could not be read. Try again.</div>
        ) : (
          <>
            <div className="panel-body">
              {/* s33(1)(g)(ii) and (iii): ordinary and overtime hours, and hours
                  on a Sunday or public holiday, stated separately. */}
              <div className="kpis">
                <div className="kpi">
                  <small>Ordinary hours</small>
                  <strong>{hoursLabel(slip.hours.ordinary)}</strong>
                </div>
                <div className="kpi">
                  <small>Overtime</small>
                  <strong>{hoursLabel(slip.hours.overtime)}</strong>
                </div>
                <div className="kpi">
                  <small>Sunday</small>
                  <strong>{hoursLabel(slip.hours.sunday)}</strong>
                </div>
                <div className="kpi">
                  <small>Public holiday</small>
                  <strong>{hoursLabel(slip.hours.holiday)}</strong>
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="rows">
                <thead>
                  <tr>
                    <th style={{ paddingLeft: 22 }}>Earnings</th>
                    <th>Hours</th>
                    <th style={{ paddingRight: 22, textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {slip.earnings.map((line, i) => (
                    <Line key={`e-${i}`} line={line} money={money} />
                  ))}
                  <tr className="total">
                    <td style={{ paddingLeft: 22 }}>Remuneration in money</td>
                    <td />
                    <td className="num" style={{ paddingRight: 22, textAlign: "right" }}>
                      {money.format(slip.gross)}
                    </td>
                  </tr>

                  {slip.additions.map((line, i) => (
                    <Line key={`a-${i}`} line={line} money={money} sign="+" />
                  ))}
                  {slip.deductions.map((line, i) => (
                    <Line key={`d-${i}`} line={line} money={money} sign="−" />
                  ))}

                  <tr className="total">
                    <td style={{ paddingLeft: 22 }}>Net pay</td>
                    <td />
                    <td className="num" style={{ paddingRight: 22, textAlign: "right" }}>
                      {money.format(slip.net)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {warnings.map((w) => (
              <div key={w.text} className="notice warn" style={{ margin: "0 22px 14px" }}>
                <strong>{w.section}</strong> — {w.text}
              </div>
            ))}

            <div className="panel-foot" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn inline" type="button" onClick={print}>Print</button>
              <button className="btn inline quiet" type="button" onClick={() => setAdding(true)}>
                Add or deduct something
              </button>
              <span className="panel-note" style={{ marginLeft: "auto" }}>
                Shifty records hours and what they come to, not the payment itself.
              </span>
            </div>
          </>
        )}
      </div>

      {adding && slip ? (
        <AdjustmentDialog
          employeeId={employeeId}
          name={slip.employee.name}
          period={monthPeriod(year, month)}
          existing={result?.adjustments ?? []}
          remunerationInMoney={slip.gross}
          currency={ctx.currency}
          timezone={ctx.timezone}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      ) : null}
    </>
  );
}

function Line({ line, money, sign }: {
  line: PayslipLine;
  money: Intl.NumberFormat;
  sign?: "+" | "−";
}) {
  return (
    <tr>
      <td style={{ paddingLeft: 22 }}>
        {line.label}
        {line.detail ? <div className="panel-note">{line.detail}</div> : null}
      </td>
      <td className="num">{line.hours != null ? hoursLabel(line.hours) : "—"}</td>
      <td className="num" style={{ paddingRight: 22, textAlign: "right" }}>
        {sign ? `${sign} ` : ""}{money.format(line.amount)}
      </td>
    </tr>
  );
}

/**
 * Adding to, or taking off, what the period comes to.
 *
 * Every warning here is shown beside the save button and none of them stops
 * it. Shifty is a register, not a regulator: one that refuses to record what
 * happened only moves the truth somewhere nobody can see it.
 */
function AdjustmentDialog({
  employeeId, name, period, existing, remunerationInMoney, currency, timezone, onClose, onSaved,
}: {
  employeeId: string;
  name: string;
  period: { from: string; to: string };
  existing: PayAdjustment[];
  remunerationInMoney: number;
  currency: string;
  timezone: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const first = name.split(" ")[0] ?? name;
  const [direction, setDirection] = useState<"deduction" | "addition">("deduction");
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  // undefined is "has not chosen"; null is "chose not to say". Both save as no
  // basis and both raise the s34(1) warning, but only the second is a decision.
  const [basis, setBasis] = useState<AdjustmentBasis | null | undefined>(undefined);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => { firstField.current?.focus(); }, []);

  const value = Number(amount.replace(/[^\d.]/g, "")) || 0;

  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  // An adjustment entered in October for September belongs to September.
  const onDate = today >= period.from && today <= period.to ? today : period.to;

  const warnings = adjustmentWarnings(
    { direction, basis: basis ?? undefined, amount: value },
    {
      remunerationInMoney,
      lossOrDamageTotal:
        lossOrDamageTotal(existing) +
        (basis === "loss_or_damage" && direction === "deduction" ? value : 0),
    },
  );

  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency", currency, minimumFractionDigits: 2,
  });

  async function save() {
    setTouched(true);
    setProblem(null);
    if (value <= 0 || purpose.trim() === "") return;
    setBusy(true);
    const r = await saveAdjustment(employeeId, {
      onDate, direction, amount: value, purpose, basis: basis ?? undefined,
    });
    setBusy(false);
    if (!r.ok) {
      setProblem({
        NOT_PERMITTED: "You need the right to change what people are paid in this household.",
        INVALID: "Check the amount and what it is for.",
        NETWORK: "Couldn't reach Shifty. Check your connection.",
      }[r.reason]);
      return;
    }
    onSaved();
  }

  return (
    <div
      className="sheet-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label="Add or deduct something">
        <div className="sheet-head">
          <div>
            <h2>{direction === "deduction" ? "Take something off" : "Add something on"}</h2>
            <p className="panel-note">{name}</p>
          </div>
          <button className="btn quiet inline" type="button" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="sheet-body">
          <div className="seg">
            {(["deduction", "addition"] as const).map((d) => (
              <button
                key={d} type="button"
                className={`seg-btn${direction === d ? " on" : ""}`}
                onClick={() => setDirection(d)}
              >
                {d === "deduction" ? "Deduct" : "Add"}
              </button>
            ))}
          </div>

          <label className="field">
            <span>Amount</span>
            <input
              ref={firstField}
              inputMode="decimal"
              value={amount}
              placeholder="0.00"
              onChange={(e) => setAmount(e.target.value)}
            />
            {touched && value <= 0 ? <em>Enter an amount</em> : null}
          </label>

          {/* s33(1)(e): the payslip must state the amount AND purpose of any
              deduction. This is the purpose, and they will read it. */}
          <label className="field">
            <span>What it is for</span>
            <input
              value={purpose}
              placeholder={direction === "deduction" ? "Advance repayment" : "Transport allowance"}
              onChange={(e) => setPurpose(e.target.value)}
            />
            {touched && purpose.trim() === ""
              ? <em>Say what it is for</em>
              : <small>This appears on {first}&rsquo;s payslip.</small>}
          </label>

          {/* s34(1) says nothing about paying somebody extra, so an addition is
              not asked to justify itself. */}
          {direction === "deduction" ? (
            <div className="field">
              <span>Why you may deduct it</span>
              <div className="choices">
                {BASES.map((b) => (
                  <button
                    key={b ?? "none"} type="button"
                    className={`choice${basis === b ? " on" : ""}`}
                    onClick={() => setBasis(b)}
                  >
                    {b ? BASIS_LABEL[b] : "I’d rather not say"}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {warnings.map((w) => (
            <div key={w.section + w.text} className="notice warn">
              <strong>{w.section}</strong> — {w.text}
            </div>
          ))}
          {warnings.length > 0 ? (
            <p className="panel-note">Shifty warns, it does not decide. You can still save this.</p>
          ) : null}

          {problem ? <div className="notice error">{problem}</div> : null}
        </div>

        <div className="sheet-foot">
          <span className="panel-note">Recorded against {onDate} · {first} can see it</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn quiet inline" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button className="btn inline" type="button" onClick={save} disabled={busy}>
              {busy
                ? "Saving…"
                : value > 0
                  ? `${direction === "deduction" ? "Deduct" : "Add"} ${money.format(value)}`
                  : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
